import { formatMoney } from '@lib/money'
import { userShare } from '@orbit/money'
import { formatInstant, unixToDate } from '@lib/datetime'
import { MemberBreakdownItem } from '@/utils/balanceCalculations'
import { GetExpenseResponse, GetPayUpType, GetUserType } from '@orbit/api-types'
import { AddPayUpPrefill } from './AddPayUpModal'

export const formatAmount = (cents: number, currencyCode: string): string =>
	formatMoney(Math.abs(cents), currencyCode)

export const formatDateKey = (unixSeconds: number): string =>
	formatInstant(unixToDate(unixSeconds), 'dateLong')

export const buildPrefillFromBreakdown = (
	targetUserId: number,
	item: MemberBreakdownItem,
): AddPayUpPrefill => {
	const fromUserId = item.amount > 0 ? item.otherUserId : targetUserId
	const toUserId = item.amount > 0 ? targetUserId : item.otherUserId
	return {
		fromUserId,
		toUserId,
		currencyCode: item.currencyCode,
		amount: Math.abs(item.amount),
	}
}

/**
 * The person fields the activity cards actually render (avatar + names).
 * Structural — the member pages pass `GetUserType`s, the public expenses page
 * passes overview travelers, which carry exactly these fields.
 */
export type ActivityPerson = Pick<GetUserType, 'id' | 'firstName' | 'lastName' | 'profilePicture'>

export type ExpenseInvolvement = { type: 'lent' | 'borrowed' | 'none'; amount: number }

export const getUserInvolvement = (
	expense: GetExpenseResponse,
	me: GetUserType | undefined,
): ExpenseInvolvement => {
	if (!me) return { type: 'none', amount: 0 }

	const isParticipant = expense.participants.some(p => p.userId === me.id)
	const isPayer = expense.paidByUserId === me.id

	if (!isParticipant && !isPayer) return { type: 'none', amount: 0 }

	const share = userShare(expense, me.id)

	if (isPayer) {
		const lentAmount = expense.amount - share
		return { type: 'lent', amount: lentAmount }
	}

	return { type: 'borrowed', amount: share }
}

export const getInvolvementText = (
	expense: GetExpenseResponse,
	me: GetUserType | undefined,
): string => {
	const involvement = getUserInvolvement(expense, me)
	const formatted = formatAmount(Math.round(involvement.amount), expense.currencyCode)
	switch (involvement.type) {
		case 'lent':
			return `you lent ${formatted}`
		case 'borrowed':
			return `you borrowed ${formatted}`
		case 'none':
			return 'not involved'
	}
}

export type PayUpInvolvement = { type: 'paid' | 'received' | 'none'; text: string }

export const getPayUpInvolvement = (
	payUp: GetPayUpType,
	me: GetUserType | undefined,
): PayUpInvolvement => {
	if (!me) return { type: 'none', text: 'between others' }
	if (payUp.fromUserId === me.id) return { type: 'paid', text: 'you paid' }
	if (payUp.toUserId === me.id) return { type: 'received', text: 'you received' }
	return { type: 'none', text: 'between others' }
}

export type ActivityItem =
	| { kind: 'expense'; date: number; data: GetExpenseResponse }
	| { kind: 'payup'; date: number; data: GetPayUpType }

export type ActivityDateGroup = { dateKey: string; items: ActivityItem[] }

/**
 * Merge expenses and pay ups into one activity stream, newest first, grouped by
 * display date. The single owner of the merge/sort/group shape — the member
 * Balances page (via `useGroupedActivity`, which layers its filter on through
 * `includeItem`) and the public expenses page both render from this.
 */
export const groupActivityByDate = (
	expenses: GetExpenseResponse[],
	payUps: GetPayUpType[],
	includeItem: (item: ActivityItem) => boolean = () => true,
): ActivityDateGroup[] => {
	const items: ActivityItem[] = [
		...expenses.map<ActivityItem>(e => ({ kind: 'expense', date: e.expenseDate, data: e })),
		...payUps.map<ActivityItem>(p => ({ kind: 'payup', date: p.payUpDate, data: p })),
	]
		.filter(includeItem)
		.sort((a, b) => b.date - a.date)

	const groups: ActivityDateGroup[] = []
	const indexByKey = new Map<string, number>()
	for (const item of items) {
		const key = formatDateKey(item.date)
		const existing = indexByKey.get(key)
		if (existing === undefined) {
			indexByKey.set(key, groups.length)
			groups.push({ dateKey: key, items: [item] })
		} else {
			groups[existing].items.push(item)
		}
	}
	return groups
}
